import {
  parseAsBoolean,
  parseAsNativeArrayOf,
  parseAsString,
  useQueryState,
} from "nuqs";
import { useMemo, useCallback } from "react";

/** URL パラメータ名（設備） */
const PARAM_NAME = "facility";
/** URL パラメータ名（EV充電器） */
const EV_PARAM_NAME = "ev";

// nuqs parser for facility filter (?facility=slug1&facility=slug2)
const facilityFilterParser = parseAsNativeArrayOf(parseAsString);

/**
 * 設備フィルタ（EV充電器を含む）の状態管理とURL同期を行うカスタムフック
 *
 * UI 上は EV 充電器も設備セクションに属するが、EV は RoomType 化されておらず
 * BE では facilitySlugs とは別引数（evChargerAvailable）で扱うため、
 * URL 上も `facility`（slug の繰り返しキー）とは独立した boolean（`ev`）で持つ。
 */
export function useFacilityFilter() {
  const [urlFilter, setUrlFilter] = useQueryState(
    PARAM_NAME,
    facilityFilterParser,
  );
  const [evChargerAvailable, setEvChargerAvailable] = useQueryState(
    EV_PARAM_NAME,
    parseAsBoolean.withDefault(false),
  );

  // URL の文字列配列を Set<string> に変換
  const facilityFilter = useMemo(
    () => new Set(urlFilter.filter((param) => param.length > 0)),
    [urlFilter],
  );

  const toggleFacility = useCallback(
    (slug: string) => {
      const next = new Set(facilityFilter);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      void setUrlFilter(next.size > 0 ? [...next] : null);
    },
    [facilityFilter, setUrlFilter],
  );

  const toggleEvCharger = useCallback(() => {
    void setEvChargerAvailable(evChargerAvailable ? null : true);
  }, [evChargerAvailable, setEvChargerAvailable]);

  // ポップアップ「決定」・セクション「クリア」用の一括適用。
  const applyFacilityConditions = useCallback(
    (facilities: string[], evCharger: boolean) => {
      void setUrlFilter(facilities.length > 0 ? facilities : null);
      void setEvChargerAvailable(evCharger ? true : null);
    },
    [setUrlFilter, setEvChargerAvailable],
  );

  return {
    /** 現在の設備フィルタ状態（slug の Set） */
    facilityFilter,
    /** 設備の選択状態をトグルし、URL に同期する */
    toggleFacility,
    /** EV充電器フィルタの状態 */
    evChargerAvailable,
    /** EV充電器フィルタの状態をトグルし、URL に同期する */
    toggleEvCharger,
    /** 設備・EV充電器フィルタを一括で適用する（ポップアップ決定・クリア用） */
    applyFacilityConditions,
  };
}
